import { getCookie } from './cookies.js';

/**
 * Currency formatting utilities
 * 
 * TODO: Replace Intl.NumberFormat with currency.js (src/currency.min.js)
 * Current implementation assumes prices are in whole units (e.g., 1435 = $1,435.00)
 * but actual data comes as cents (e.g., 1435 cents = $14.35)
 * currency.js handles precision correctly with fromCents option
 * 
 * Uses Proxy-based state management
 */

// Currency symbols
const CURRENCY_SYMBOLS = {
  'MXN': '$',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'CAD': 'C$',
  'AUD': 'A$',
  'BRL': 'R$',
  'ARS': '$',
  'CLP': '$',
  'COP': '$',
  'PEN': 'S/',
  'UYU': '$U'
};

const DEFAULT_CURRENCY = 'MXN';

// Local references (will be set during init)
let proxyState = null;
let availableCurrencies = [DEFAULT_CURRENCY];

/**
 * Get current currency from proxy state
 */
export function getCurrentCurrency() {
  if (proxyState) {
    return proxyState.currency || DEFAULT_CURRENCY;
  }
  return DEFAULT_CURRENCY;
}

/**
 * Get available currencies
 */
export function getAvailableCurrencies() {
  return availableCurrencies;
}

/**
 * Set current currency via proxy
 * @param {string} currency - Currency code
 */
export function setCurrency(currency) {
  if (!availableCurrencies.includes(currency)) {
    console.warn(`[Currency] ${currency} not available`);
    return;
  }
  
  if (proxyState) {
    proxyState.currency = currency;
  }
}

/**
 * Format amount with currency
 * @param {number} amount - Amount (not in cents)
 * @param {string} currency - Currency code (optional, uses current)
 * @returns {string} Formatted price
 */
export function formatPrice(amount, currency = null) {
  const curr = currency || getCurrentCurrency();
  const symbol = CURRENCY_SYMBOLS[curr] || curr;
  
  // Format number with locale - amount is already in whole units, not cents
  const formatted = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  return `${symbol}${formatted}`;
}

/**
 * Format amount with currency (short format)
 * @param {number} amount - Amount
 * @param {string} currency - Currency code (optional)
 * @returns {string} Formatted price
 */
export function formatPriceShort(amount, currency = null) {
  const curr = currency || getCurrentCurrency();
  const symbol = CURRENCY_SYMBOLS[curr] || curr;
  
  return `${symbol}${amount}`;
}

/**
 * Initialize currency from branch config
 * Priority: URL param > Cookie > Backend default > Hardcoded default
 * @param {Object} branchConfig - Branch configuration
 * @param {Object} stateProxy - The Proxy state object from APP_DATA
 */
export function initCurrency(branchConfig, stateProxy) {
  proxyState = stateProxy;
  
  // Set available currencies from backend
  if (branchConfig?.availableCurrencies) {
    availableCurrencies = branchConfig.availableCurrencies;
  }
  
  // Priority 1: Check URL param (highest priority for sharing links)
  const urlCurrency = new URLSearchParams(window.location.search).get('currency');
  if (urlCurrency && availableCurrencies.includes(urlCurrency)) {
    proxyState.currency = urlCurrency;
    return;
  }
  
  // Priority 2: Check cookie
  const cookieCurrency = getCookie('currency');
  if (cookieCurrency && availableCurrencies.includes(cookieCurrency)) {
    proxyState.currency = cookieCurrency;
    return;
  }
  
  // Priority 3: Backend default
  if (branchConfig?.defaultCurrency && availableCurrencies.includes(branchConfig.defaultCurrency)) {
    proxyState.currency = branchConfig.defaultCurrency;
    return;
  }
  
  // Priority 4: Hardcoded default
  proxyState.currency = DEFAULT_CURRENCY;
}

/**
 * Convert price array to formatted string
 * @param {Array} prices - Array of {amount, currency}
 * @returns {string} Formatted price
 */
export function formatPrices(prices) {
  if (!prices || prices.length === 0) {
    return '';
  }
  
  const currentCurrency = getCurrentCurrency();
  
  // Find price in current currency
  const price = prices.find(p => p.currency === currentCurrency) || prices[0];
  
  return formatPrice(price.amount, price.currency);
}

/**
 * Get currency symbol
 * @param {string} currency - Currency code
 * @returns {string} Symbol
 */
export function getCurrencySymbol(currency = null) {
  return CURRENCY_SYMBOLS[currency || getCurrentCurrency()] || (currency || getCurrentCurrency());
}
